// @ts-strict-ignore
import { capitalize } from 'lodash'
import accounting from 'accounting'
import { Moment, MomentInput } from 'moment'
import moment from 'moment-timezone'
import { Currency, Nullable, RateMode } from 'types'
import { TimezoneName } from 'countries-and-timezones'

// Keep these in sync with the formats on the backend (see intializers/date_formats.rb file)
export const DATE_FORMAT_INTERNAL = 'YYYY-MM-DD'
export const DATE_TIME_FORMAT_SHORT = 'MMM DD h:mm a'
export const DATE_TIME_FORMAT = 'MMM Do YYYY h:mm a'
export const DATE_TIME_FORMAT_WITH_TZ = 'MMM Do YYYY h:mm a Z z'
// TODO: looks like date and short date formats are basically the same now. Maybe unify them?
export const DATE_FORMAT_SHORT = 'MMM DD, YYYY'
export const DATE_FORMAT = 'MMM Do YYYY'

const DOLLAR_SYMBOL = '$'

export type CurrencyInput = string | Currency

export type CurrencyFormattingOptions = {
  withCents?: boolean
  removeEmptyCents?: boolean
  roundToThousands?: boolean
  currency: CurrencyInput
  symbol?: false | string
}

export const slugify = (value: string) => {
  return String(value)
    .normalize('NFKD') // split accented characters into their base characters and diacritical marks
    .replace(/[\u0300-\u036f]/g, '') // remove all the accents, which happen to be all in the \u03xx UNICODE block.
    .trim() // trim leading or trailing whitespace
    .toLowerCase() // convert to lowercase
    .replace(/[^a-z0-9 -]/g, '') // remove non-alphanumeric characters
    .replace(/\s+/g, '-') // replace spaces with hyphens
    .replace(/-+/g, '-') // remove consecutive hyphens
}

export const normalizeCurrency = (input: CurrencyInput): Currency => typeof input === 'string' ? { code: input } : input

export const currencyToSymbol = (currencyInput: CurrencyInput) => {
  const currency = normalizeCurrency(currencyInput)
  if (!currency || !currency?.code) return DOLLAR_SYMBOL

  const code = currency.code.toUpperCase()

  // Keep this in sync with config/initializers/money.rb in the backend
  if (code === 'MXN') return 'MX$'
  if (code === 'CAD') return 'CA$'
  if (code === 'COP') return 'COL$'
  if (code === 'AUD') return 'AUD$'

  // This trick leverages the browser's locale based formatting to get the symbol
  // From: https://stackoverflow.com/a/19374099
  try {
    const num = 0
    return num.toLocaleString(
      'en',
      {
        style: 'currency',
        currency: code,
      },
    ).replace(/\d+([,.]\d+)?/g, '')
  } catch (err) {
    console.error(err)
  }

  return DOLLAR_SYMBOL
}

export const currencyToShortSymbol = (currency: CurrencyInput) => {
  const symbol = currencyToSymbol(currency)

  // Manually shorten the symbol if it's too long
  // Keep this in sync with currencyToSymbol
  if (symbol === 'MX$') return '$'
  if (symbol === 'CA$') return '$'
  if (symbol === 'COL$') return '$'
  if (symbol === 'AUD$') return '$'
  if (symbol?.length > 1) return DOLLAR_SYMBOL

  return symbol
}

/**
 * Rounds a monetary value making sure it does not have more than 2 decimals
 *
 * @param amount the amount to round
 */
export const roundMonetaryValue = (amount: number) => {
  if (!amount) return amount

  return Math.round(amount * 100) / 100
}

const getDefaultCurrencyFormattingOptions = () => ({
  withCents: true,
  removeEmptyCents: true,
} as CurrencyFormattingOptions)

export const formatAsCurrency = (value: Nullable<number>, optionsOverride: CurrencyFormattingOptions) : string => {
  if (!optionsOverride?.currency) return null
  if (isNaN(value)) return null
  if (value === undefined || value === null) return null

  const options = { ...getDefaultCurrencyFormattingOptions(), ...optionsOverride }

  if (options?.roundToThousands) {
    value = Math.round(value / 1000)
  }

  // accounting docs: http://openexchangerates.github.io/accounting.js/#methods
  let formatted = accounting.formatMoney(value, {
    symbol: options?.symbol === false ? '' : (currencyToSymbol(options?.currency) || '$'),
    decimal: '.',
    thousand: ',',
    precision: options?.withCents ? 2 : 0,
  })
  if (options?.removeEmptyCents) formatted = formatted.replace(/\.00$/, '')

  if (options?.roundToThousands) {
    formatted += 'k'
  }

  return formatted
}

export function formatRateMode(rateMode: RateMode) {
  if (!rateMode) return null
  if (rateMode === 'day') return 'daily'

  return `${rateMode}ly`
}

export function formatShortRateMode(rateMode: RateMode) {
  if (rateMode === 'hour') return 'hr'

  return rateMode
}

export function formatAsRate(value, currency: CurrencyInput | null | undefined, rateMode: RateMode | null | undefined) {
  if ((!value && value !== 0) || !currency || !rateMode) return null

  return `${formatAsCurrency(value, { currency, withCents: rateMode !== 'year' })}/${formatShortRateMode(rateMode)}`
}

export const formatAsReadableFileSize = (bytes: number, isSi = true) => {
  const multiplication = isSi ? 1000 : 1024
  if (Math.abs(bytes) < multiplication) {
    return `${bytes} B`
  }
  const units = isSi ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'] : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB']
  let unitIndex = -1
  do {
    bytes /= multiplication
    ++unitIndex
  } while (Math.abs(bytes) >= multiplication && unitIndex < units.length - 1)
  return `${bytes.toFixed(1)}  ${units[unitIndex]}`
}

const isValidInput = (date: MomentInput) => date !== null && date !== undefined && moment(date).isValid()

export const formatAsDateTime = (date: MomentInput) => (
  isValidInput(date) ? moment(date).format(DATE_TIME_FORMAT) : null
)

/**
 * Format the given timestamp in the given timezone. The timestamp will be displayed as the local time in the given
 * timezone, and the timezone is displayed in the resulting string.
 *
 * @param date the timestamp to format
 * @param timezone the timezone to convert it to
 */
export const formatAsDateTimeWithTimezone = (date: MomentInput, timezone: TimezoneName) => {
  if (!timezone) return formatAsDateTime(date)

  return (
    isValidInput(date) ? moment(date).tz(timezone).format(DATE_TIME_FORMAT_WITH_TZ) : null
  )
}

export const formatAsShortDateTime = (date: MomentInput) => (
  isValidInput(date) ? moment(date).format(DATE_TIME_FORMAT_SHORT) : null
)

export const formatAsDate = (date: MomentInput) => (
  isValidInput(date) ? moment(date).format(DATE_FORMAT) : null
)

export const formatAsShortDate = (date: MomentInput) => (
  isValidInput(date) ? moment(date).format(DATE_FORMAT_SHORT) : null
)

export function formatAsDayTime(time: MomentInput) {
  if (!isValidInput(time)) return null

  return moment(time).format('HH:mm')
}

export function formatAsTime(time: MomentInput) {
  if (!isValidInput(time)) return null

  return moment(time).format('h:mm A')
}

export function formatAsWeekDay(time: MomentInput) {
  if (!isValidInput(time)) return null

  return moment(time).format('ddd, MMM Do')
}

export const formatAsTitleCase = (text: string) => text?.split(' ')?.map((part: any) => capitalize(part))?.join(' ')

/**
 * A DateLike is union of multiple types that can contain a date or timestamp value
 */
export type DateLike = string | Date | Moment | MomentInput

/**
 * Converts a DateLike to a Moment instance
 * @param value any DateLike value
 * @return a Moment instance
 */
export function ensureMoment(value: DateLike) {
  if (isValidInput(value)) return moment(value)

  return null
}

/**
 * Converts a DateLike to a JavaScript Date instance
 * @param value any DateLike value
 * @return a JavaScript Date
 */
export const ensureDate = (value: DateLike) => (
  // NOTE: returning null is important because undefined will be dropped from hashes when used in mutation inputs
  ensureMoment(value)?.toDate() || null
)

/**
 * Converts a DateLike to a string that contains the given DateLike, formatted with the server's format.
 *
 * Meant to be used to send values as plain dates to the backend.
 *
 *
 * @param value any DateLike value
 * @return a string with a plain date, formatted with the server's format
 */
export function ensureDateAsString(value: DateLike) {
  // NOTE: returning null is important because undefined will be dropped from hashes when used in mutation inputs
  return ensureMoment(value)?.format(DATE_FORMAT_INTERNAL) || null
}

/**
 * Converts a DateLike to a string that contains the given timestamp, formatted with the server's format.
 *
 * Meant to be used to send values as timestamps (DateTime) to the backend.
 *
 * @param value any DateLike value
 * @return a string with a timestamp, formatted with the server's format
 */
export const ensureDateTimeAsString = (value: DateLike) => ensureMoment(value)?.toISOString(true)

export const getTimeParts = (durationInSeconds: number) => {
  if (!durationInSeconds || durationInSeconds < 0) return null

  const hours = Math.floor(durationInSeconds / 3600)
  const minutes = Math.floor(durationInSeconds / 60)
  const seconds = durationInSeconds % 60

  return {
    hours,
    minutes,
    seconds,
  }
}

export function formatDuration(hours: number, minutes: number) {
  if (!hours && !minutes) return null
  if (!minutes) return `${hours}h`
  if (!hours) return `${minutes}m`

  return `${hours}h${minutes}m`
}

export function formatDurationMinutes(minutes: number) {
  return formatDuration(Math.floor(minutes / 60), minutes - (Math.floor(minutes / 60) * 60))
}

export const formatDurationInSeconds = (durationInSeconds: number) : string => {
  const parts = getTimeParts(durationInSeconds)
  if (!parts) return null

  const stringParts = []
  if (parts.hours > 0) stringParts.push(`${parts.hours} hour${parts.hours > 1 ? 's' : ''}`)
  if (parts.minutes > 0) stringParts.push(`${parts.minutes} minute${parts.minutes > 1 ? 's' : ''}`)
  if (parts.seconds > 0) stringParts.push(`${Math.round(parts.seconds)} second${parts.seconds > 1 ? 's' : ''}`)

  if (stringParts.length > 0) return stringParts.join(', ')

  return null
}

export function formatAsShortDateRange(startDate: MomentInput, endDate: MomentInput, fallback: string = 'N/A') {
  const startText = formatAsShortDate(startDate) || fallback
  const endText = formatAsShortDate(endDate) || fallback

  if (startText === endText) return startText

  return `${startText} - ${endText}`
}

export function formatAsWeekDayString(day: MomentInput) {
  return new Date(day.toLocaleString()).toLocaleString('en-us', { weekday: 'long' })
}
